import { defineComponent } from 'vue';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import TranslationContainer from '../TranslationContainer.jsx';

const TreeLabelDsgn = defineComponent({
    name: 'tree-label-dsgn',
    data: () => ({
        title: null,
        titletext: null,
        schema: null,
    }),
    mounted() {
        if (this.typeSchema)
            this.Refresh();
    },
    computed: {
        ControlData() {
            return this.cmodel?.ControlData;
        },
        ActionData() {
            return this.cmodel?.ActionData;
        },
        ActionExpression() {
            return this.cmodel?.ActionExpression;
        },
    },
    methods: {
        ...methods,
        async Refresh() {
            try {
                this.schema = await utils.schema.get(this.typeSchema);
                const res = utils.schema.resolve_Of(this.schema);

                this.titletext = res.title;

                if (res.documentEditorTitle)
                    this.title = utils.compile(this, res.documentEditorTitle);
                else
                    this.title = null;
            }
            catch (e) {
                utils.warn('TreeLabelDsgn Refresh failed: ' + this.typeSchema + '; ' + e);
                this.title = null;
            }
        },
    },
    props: {
        label: { type: String, default: '' },
        text: { type: String, default: '' },
        typeSchema: { type: String, default: null },
        cmodel: { type: Object, default: null },
        parent: { type: Object, default: null },
    },
    render() {
        let titleText = this.titletext || this.text || '';
        if (this.title) {
            try {
                titleText = utils.evaluate(this.title, this.cmodel);
            }
            catch (e) {
                utils.warn('TreeLabelDsgn title evaluate failed: ' + e);
            }
        }

        let label = this.label;
        const suffix = titleText || (this.schema ? this.schema.title : '');
        if (suffix)
            label += ': ' + suffix;

        let highlight;
        if (this.cmodel?.ActionExpression && this.cmodel.ActionExpression.startsWith('false'))
            highlight = true;

        let permissions;
        if (this.cmodel?.AccessPermission?.Enabled)
            permissions = true;

        const classNames = {
            'treeview-node': true,
            selected: this.parent?.SelectedNode === this.cmodel,
            highlight,
            permissions,
            hastype: this.cmodel && '$typeSchema' in this.cmodel && !highlight,
            hasnotype: this.cmodel && !('$typeSchema' in this.cmodel) && !highlight,
        };

        return (
            <span class={classNames} onClick={() => this.parent?.SelectNode?.(this.cmodel)}>
                {label}
            </span>
        );
    },
});

const TreeNodeDsgn = defineComponent({
    name: 'tree-node-dsgn',
    components: { TreeLabelDsgn },
    data: () => ({
        schema: null,
        title: null,
    }),
    methods: {
        ...methods,
    },
    props: {
        name: { type: [String, Number], default: '' },
        root: { type: Object, default: null },
        parent: { type: Object, default: null },
        cmodel: { type: [Object, Array], default: null },
        level: { type: Number, default: 0 },
        pathSegments: { type: Array, default: () => [] },
    },
    render() {
        if (!this.cmodel) return null;

        const elements = [];

        if (Array.isArray(this.cmodel)) {
            for (let i = 0; i < this.cmodel.length; i++) {
                const node = this.cmodel[i];

                if (typeof node === 'object') {
                    const childPath = this.pathSegments.concat(i);
                    const vis = this.parent.isTreeExpanded(childPath, this.level);
                    const visctl = vis ? <i class="mdi mdi-minus-box-outline" /> : <i class="mdi mdi-plus-box" />;

                    elements.push(
                        <div key={`a-${i}`} style={{ whiteSpace: 'nowrap' }}>
                            <span class="treeview-expand-collapse" onClick={() => this.parent.toggleTreeExpanded(childPath, this.level)}>
                                {visctl}
                            </span>
                            <TreeLabelDsgn label={'(' + i + ')'} typeSchema={node.$typeSchema} parent={this.parent} cmodel={node} />
                        </div>
                    );

                    if (vis)
                        elements.push(
                            <TreeNodeDsgn
                                key={`ac-${i}`}
                                name={i}
                                root={this.root}
                                parent={this.parent}
                                cmodel={node}
                                level={this.level + 1}
                                pathSegments={childPath}
                            />
                        );
                }
            }
        }
        else {
            for (const key in this.cmodel) {
                if (key.substr(0, 1) === '$') continue;
                const node = this.cmodel[key];

                if (node && typeof node === 'object') {
                    const childPath = this.pathSegments.concat(key);
                    const vis = this.parent.isTreeExpanded(childPath, this.level);
                    const visctl = vis ? <i class="mdi mdi-minus-box-outline" /> : <i class="mdi mdi-plus-box" />;

                    elements.push(
                        <div key={`o-${key}`} style={{ whiteSpace: 'nowrap' }}>
                            <span class="treeview-expand-collapse" onClick={() => this.parent.toggleTreeExpanded(childPath, this.level)}>
                                {visctl}
                            </span>
                            <TreeLabelDsgn label={key} typeSchema={node.$typeSchema} parent={this.parent} cmodel={node} />
                        </div>
                    );
                    if (vis)
                        elements.push(
                            <TreeNodeDsgn
                                key={`oc-${key}`}
                                name={key}
                                root={this.root}
                                parent={this.parent}
                                cmodel={node}
                                level={this.level + 1}
                                pathSegments={childPath}
                            />
                        );
                }
            }
        }

        return <div style={{ marginLeft: '15px' }}>{elements}</div>;
    },
});

export default defineComponent({
    name: 'tree-view-dsgn',
    mixins: [BaseComponentMixin],
    components: {
        TreeLabelDsgn,
        TreeNodeDsgn,
        TranslationContainer,
    },
    data() {
        return {
            cmodel: null,
            selectednode: null,
            titleEval: null,
            expansionOverrides: {},
        };
    },
    async mounted() {
        if (this.controlData?.Title)
            this.titleEval = utils.compile(this, this.controlData.Title);
        await this.Refresh();
    },
    computed: {
        Model() {
            return this.cmodel;
        },
        SelectedNode() {
            return this.selectednode;
        },
    },
    methods: {
        ...methods,
        treePathKey(segments) {
            return JSON.stringify(segments);
        },
        clearExpansionState() {
            this.expansionOverrides = {};
        },
        isTreeExpanded(childSegments, parentLevel) {
            const key = this.treePathKey(childSegments);
            if (Object.prototype.hasOwnProperty.call(this.expansionOverrides, key))
                return this.expansionOverrides[key];
            return parentLevel <= 0;
        },
        toggleTreeExpanded(childSegments, parentLevel) {
            const key = this.treePathKey(childSegments);
            const cur = this.isTreeExpanded(childSegments, parentLevel);
            const next = !cur;
            const def = parentLevel <= 0;
            const nextOverrides = { ...this.expansionOverrides };
            if (next === def)
                delete nextOverrides[key];
            else
                nextOverrides[key] = next;
            this.expansionOverrides = nextOverrides;
        },
        SelectNode(node) {
            this.selectednode = node;
        },
        handleClick(e) {
            this.Root.SelectNode(this);
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault();
        },
        async Refresh() {
            this.clearExpansionState();
            try {
                if (this.controlData.SourceType == 'Url' && this.controlData.SourceUrl)
                    this.cmodel = await utils.api.get(this.controlData.SourceUrl);
                else if (this.controlData.SourceRaw)
                    this.cmodel = this.controlData.SourceRaw;
                else
                    this.cmodel = {};
            }
            catch (e) {
                this.cmodel = {};
            }
        },
    },
    props: {
    },
    render() {
        const titleDisplay = this.titleEval
            ? utils.evaluate(this.titleEval, this)
            : (this.controlData?.Title || '');

        const outerStyle = {
            borderStyle: 'solid',
            borderWidth: '1px',
            display: 'flex',
            flexDirection: 'row',
            flexGrow: '0',
            flexShrink: '1',
            fontSize: '14px',
            ...utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent),
        };

        if (!this.cmodel) {
            return (
                <div
                    class={{ 'c-TreeView': true, [`c-name-${this.name || 'unnamed'}`]: true, 'designer-container': true }}
                    style={{ ...outerStyle, padding: '5px', overflow: 'auto' }}
                    onClick={(e) => this.handleClick(e)}>
                    <div class={{ 'designer-container-hovering': true, 'designer-container-selected': this.selected$ }} />
                    <div class="c-TreeView-title" style={{ fontWeight: 'bold', fontSize: '14px', padding: '4px 0' }}>
                        <TranslationContainer context={this} value="Loading..." />
                    </div>
                </div>
            );
        }

        return (
            <div
                class={{
                    'c-TreeView': true,
                    [`c-name-${this.name || 'unnamed'}`]: true,
                    'designer-container': true,
                    selected: this.selected$,
                }}
                style={{ ...outerStyle, padding: '5px', overflow: 'auto' }}
                onClick={(e) => this.handleClick(e)}>
                <div class={{ 'designer-container-hovering': true, 'designer-container-selected': this.selected$ }} />
                <div class="property-grid" style={{ overflow: 'hidden', padding: '0', display: 'flex', flexDirection: 'column' }}>
                    <div
                        class="c-TreeView-title"
                        style={{
                            fontWeight: 'bold',
                            fontSize: '14px',
                            padding: '4px 0',
                            margin: 0,
                        }}>
                        {titleDisplay}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'auto', marginTop: '6px', flexGrow: '1' }}>
                        <div style={{ whiteSpace: 'nowrap' }}>
                            <TreeLabelDsgn label="Root" typeSchema={this.cmodel.$typeSchema} parent={this} cmodel={this.cmodel} />
                        </div>
                        <TreeNodeDsgn
                            name={this.controlData.Name || 'Tree'}
                            root={this.root}
                            parent={this}
                            cmodel={this.cmodel}
                            level={0}
                            pathSegments={[]}
                        />
                    </div>
                </div>
            </div>
        );
    },
});
